// Plans service for Internet and Electricity plans
// Implements multi-API merge strategy matching original JS app

import type {
  AddressData,
  InternetPlan,
  InternetPlansResponse,
  ElectricityPlansResponse,
  UsageProfile,
  PropertyData,
} from '@/types';
import { api } from '@/api/client';
import { selectTopPlans, type MergedElectricityPlan } from '@/utils/planSelection';
import { normalizeSchedule, type ScheduleSource } from '@/utils/scheduling';
import { logPlanScheduleDiagnostics } from '@/utils/scheduleLogging';
import { logger } from '@/utils/logger';

// Helper to normalize plan IDs - ensures id field is always set
function normalizePlanId<T extends { id?: string; ID?: string } & ScheduleSource>(plan: T): T & { id: string } {
  return {
    ...plan,
    id: plan.id || plan.ID || '',
    ...normalizeSchedule(plan),
  };
}

// Provider name mapping between FCC API and 2TurnItOn API
const PROVIDER_NAME_MAP: Record<string, string> = {
  'Cox Communications': 'Cox',
  'Frontier': 'Frontier Communications',
  'AT&T': 'AT&T',
  'Spectrum': 'Spectrum',
};

// ============================================
// 2TurnItOn API Helpers
// ============================================

interface TurnItOnPlan extends ScheduleSource {
  id?: string;
  ID?: string;
  vendorId: string;
  vendorName: string;
  name: string;
  logo?: string;
  mPrice?: number;
  price?: number;
  offerCode?: string;
  duns?: string;
}

async function fetch2TurnItOnPlans(
  serviceName: 'Internet' | 'Electricity',
  zipCode: string,
  userId?: string,
  isBusiness?: boolean,
  isRental?: boolean
): Promise<TurnItOnPlan[]> {
  try {
    const params = new URLSearchParams({ serviceName, zipCode });
    if (isBusiness !== undefined) params.append('isBusiness', String(isBusiness));
    if (isRental !== undefined) params.append('isRental', String(isRental));
    const url = `/api/2turniton/services/${serviceName}/plans?${params}`;

    const response = await api.get<TurnItOnPlan[] | { data: TurnItOnPlan[] }>(url, userId ? { userId } : {});

    logger.info({ area: 'plans' }, `2TurnItOn ${serviceName} response received`);

    if (!response.success) {
      logger.warn({ area: 'plans' }, `2TurnItOn ${serviceName} - No plans found`);
      return [];
    }

    const data = response.data;
    // Handle both array and wrapped response formats
    if (Array.isArray(data)) {
      logPlanScheduleDiagnostics({
        serviceName,
        stage: '2turniton-response',
        zipCode,
        userId,
        plans: data,
      });
      return data;
    } else if (data && Array.isArray((data as { data: TurnItOnPlan[] }).data)) {
      const plans = (data as { data: TurnItOnPlan[] }).data;
      logPlanScheduleDiagnostics({
        serviceName,
        stage: '2turniton-wrapped-response',
        zipCode,
        userId,
        plans,
      });
      return plans;
    }

    logger.warn({ area: 'plans' }, `2TurnItOn ${serviceName} - No plans in response`);
    return [];
  } catch (error) {
    logger.error({ area: 'plans' }, `2TurnItOn ${serviceName} Error`, error instanceof Error ? error : undefined);
    return [];
  }
}

// ============================================
// Internet Plans - FCC + 2TurnItOn Merge
// ============================================

interface FCCRecord {
  brand_name: string;
  max_advertised_download_speed: number;
  max_advertised_upload_speed: number;
  technology: string;
  percentile_score?: number;
}

interface FCCBroadbandData {
  serviceability_records?: FCCRecord[];
  record_count?: number;
}

async function fetchFCCBroadband(addressData: AddressData): Promise<FCCRecord[]> {
  try {
    const response = await api.post<FCCBroadbandData>('/api/fcc-broadband/serviceability', {
      address1: addressData.street || addressData.fullAddress,
      city: addressData.city,
      state: addressData.stateCode,
      zip: addressData.zip,
    });

    logger.info({ area: 'plans' }, 'FCC Broadband response received');

    if (response.success && response.data?.serviceability_records) {
      return response.data.serviceability_records;
    }

    return [];
  } catch (error) {
    logger.error({ area: 'plans' }, 'FCC Broadband Error', error instanceof Error ? error : undefined);
    return [];
  }
}

function groupByProvider(records: FCCRecord[]): Record<string, FCCRecord[]> {
  return records.reduce((acc, record) => {
    const key = record.brand_name;
    if (!acc[key]) acc[key] = [];
    acc[key].push(record);
    return acc;
  }, {} as Record<string, FCCRecord[]>);
}

export async function fetchInternetPlans(
  addressData: AddressData,
  userId?: string,
  isBusiness?: boolean,
  isRental?: boolean
): Promise<InternetPlansResponse> {
  try {
    // Step 1: Call both APIs concurrently
    const [fccRecords, turnItOnPlans] = await Promise.all([
      fetchFCCBroadband(addressData),
      fetch2TurnItOnPlans('Internet', addressData.zip, userId, isBusiness, isRental),
    ]);

    logger.info({ area: 'plans' }, `Internet Plans - FCC records: ${fccRecords.length}, 2TurnItOn plans: ${turnItOnPlans.length}`);

    // If no 2TurnItOn data, we can't show plans (no pricing/logos)
    if (turnItOnPlans.length === 0) {
      // Fall back to FCC-only display
      if (fccRecords.length > 0) {
        const fccOnlyPlans: InternetPlan[] = fccRecords.map((record) => ({
          id: `fcc-${record.brand_name}`,
          name: record.brand_name,
          vendorId: record.brand_name,
          vendorName: record.brand_name,
          downloadSpeed: record.max_advertised_download_speed,
          uploadSpeed: record.max_advertised_upload_speed,
          technology: record.technology,
          percentileScore: record.percentile_score || null,
          hasFccData: true,
        }));
        logPlanScheduleDiagnostics({
          serviceName: 'Internet',
          stage: 'fcc-only-fallback',
          zipCode: addressData.zip,
          userId,
          plans: fccOnlyPlans,
        });
        return { success: true, plans: fccOnlyPlans };
      }
      return { success: false, error: 'No internet plans available' };
    }

    // Step 2: Group FCC providers
    const fccByProvider = groupByProvider(fccRecords);

    // Step 3: Match and merge
    const mergedPlans: InternetPlan[] = [];

    if (fccRecords.length > 0) {
      // Full merge with FCC data
      Object.keys(fccByProvider).forEach((fccName) => {
        const tioName = PROVIDER_NAME_MAP[fccName] || fccName;
        const tioPlans = turnItOnPlans.filter((p) => p.vendorName === tioName);

        if (tioPlans.length === 0) return;

        // Get fastest FCC record
        const fastestFcc = fccByProvider[fccName].reduce((a, b) =>
          a.max_advertised_download_speed > b.max_advertised_download_speed ? a : b
        );

        // Get lowest price plan from 2TurnItOn
        const lowestPricePlan = tioPlans.reduce((a, b) => {
          const priceA = a.mPrice ?? a.price ?? Infinity;
          const priceB = b.mPrice ?? b.price ?? Infinity;
          return priceA < priceB ? a : b;
        });

        mergedPlans.push({
          id: lowestPricePlan.ID || lowestPricePlan.id || `merged-${tioName}`,
          vendorId: lowestPricePlan.vendorId,
          vendorName: tioName,
          name: lowestPricePlan.name || tioName,
          logo: lowestPricePlan.logo,
          downloadSpeed: fastestFcc.max_advertised_download_speed,
          uploadSpeed: fastestFcc.max_advertised_upload_speed,
          technology: fastestFcc.technology,
          percentileScore: fastestFcc.percentile_score || null,
          price: lowestPricePlan.mPrice ?? lowestPricePlan.price,
          hasFccData: true,
          ...normalizeSchedule(lowestPricePlan),
        });
      });

      // Sort by percentile score (highest first)
      mergedPlans.sort((a, b) => (b.percentileScore || 0) - (a.percentileScore || 0));
    } else {
      // No FCC data - use 2TurnItOn only
      const vendorGroups: Record<string, TurnItOnPlan[]> = {};
      turnItOnPlans.forEach((plan) => {
        if (!vendorGroups[plan.vendorName]) vendorGroups[plan.vendorName] = [];
        vendorGroups[plan.vendorName].push(plan);
      });

      Object.keys(vendorGroups).forEach((vendorName) => {
        const plans = vendorGroups[vendorName];
        const lowestPricePlan = plans.reduce((a, b) => {
          const priceA = a.mPrice ?? a.price ?? Infinity;
          const priceB = b.mPrice ?? b.price ?? Infinity;
          return priceA < priceB ? a : b;
        });

        mergedPlans.push({
          id: lowestPricePlan.ID || lowestPricePlan.id || `tio-${vendorName}`,
          vendorId: lowestPricePlan.vendorId,
          vendorName: vendorName,
          name: lowestPricePlan.name || vendorName,
          logo: lowestPricePlan.logo,
          price: lowestPricePlan.mPrice ?? lowestPricePlan.price,
          hasFccData: false,
          ...normalizeSchedule(lowestPricePlan),
        });
      });

      // Sort by price (lowest first)
      mergedPlans.sort((a, b) => (a.price || Infinity) - (b.price || Infinity));
    }

    logger.info({ area: 'plans' }, `Internet Plans - Merged plans: ${mergedPlans.length}`);
    logPlanScheduleDiagnostics({
      serviceName: 'Internet',
      stage: 'merged-plans',
      zipCode: addressData.zip,
      userId,
      plans: mergedPlans,
    });
    // Normalize all plan IDs before returning
    return { success: true, plans: mergedPlans.map(normalizePlanId) };
  } catch (error) {
    logger.error({ area: 'plans' }, 'Internet Plans Error', error instanceof Error ? error : undefined);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
}

// ============================================
// Electricity Plans - 2TurnItOn + ComparePower Merge
// ============================================

interface ComparePowerPlan {
  _id: string;
  average_cents_per_kWh: number;
  total_cost: number;
  product?: {
    term?: number;
    percent_green?: number;
    headline?: string;
    early_termination_fee?: number;
    name?: string;
  };
  display_pricing_500?: number;
  display_pricing_1000?: number;
  display_pricing_2000?: number;
  document_links?: Array<{ name: string; url: string }>;
}

async function fetchComparePowerForDuns(duns: string, usage: number): Promise<ComparePowerPlan[]> {
  try {
    const params = new URLSearchParams({
      tdsp_duns: duns,
      display_usage: String(usage),
      group: 'default',
    });

    const response = await api.get<ComparePowerPlan[]>(
      `/api/comparepower/plans?${params}`
    );

    if (response.success && Array.isArray(response.data)) {
      logger.info({ area: 'plans' }, `ComparePower - Fetched ${response.data.length} plans for DUNS ${duns}`);
      return response.data;
    }

    return [];
  } catch (error) {
    logger.error({ area: 'plans' }, `ComparePower Error for DUNS ${duns}`, error instanceof Error ? error : undefined);
    return [];
  }
}

export async function fetchElectricityPlans(
  addressData: AddressData,
  propertyData?: PropertyData | null,
  usageProfile?: UsageProfile | null,
  userId?: string,
  explicitUsage?: number,
  isBusiness?: boolean,
  isRental?: boolean
): Promise<ElectricityPlansResponse> {
  try {
    // Calculate estimated usage
    let estimatedUsage = 1000;
    let usageSource: 'actual' | 'calculated' = 'calculated';

    // Use explicit usage if provided (from usage level selector)
    if (explicitUsage && explicitUsage > 0) {
      estimatedUsage = explicitUsage;
      usageSource = 'calculated';
    } else if (usageProfile?.averageUsage) {
      estimatedUsage = usageProfile.averageUsage;
      usageSource = 'actual';
    } else if (propertyData?.squareFootage) {
      // Texas baseline: 0.5 kWh per sq ft per month
      let efficiencyMultiplier = 1.0;
      const yearBuilt = propertyData.yearBuilt;

      if (!yearBuilt || yearBuilt < 1980) {
        efficiencyMultiplier = 1.25;
      } else if (yearBuilt >= 1980 && yearBuilt <= 1999) {
        efficiencyMultiplier = 1.1;
      } else if (yearBuilt >= 2000 && yearBuilt <= 2009) {
        efficiencyMultiplier = 1.0;
      } else if (yearBuilt >= 2010 && yearBuilt <= 2019) {
        efficiencyMultiplier = 0.9;
      } else {
        efficiencyMultiplier = 0.85;
      }

      estimatedUsage = Math.round(propertyData.squareFootage * 0.5 * efficiencyMultiplier);
    }

    logger.info({ area: 'plans' }, `Electricity Plans - Using ${estimatedUsage} kWh (source: ${usageSource})`);

    // Step 1: Fetch 2TurnItOn electricity plans
    const turnItOnPlans = await fetch2TurnItOnPlans('Electricity', addressData.zip, userId, isBusiness, isRental);

    if (turnItOnPlans.length === 0) {
      logger.warn({ area: 'plans' }, 'Electricity Plans - No plans from 2TurnItOn');
      return { success: false, error: 'No electricity plans available' };
    }

    logger.info({ area: 'plans' }, `Electricity Plans - 2TurnItOn plans: ${turnItOnPlans.length}`);

    // Step 2: Extract unique DUNS values
    const uniqueDuns = [...new Set(turnItOnPlans.map((p) => p.duns).filter(Boolean))] as string[];
    logger.info({ area: 'plans' }, `Electricity Plans - Unique DUNS: ${JSON.stringify(uniqueDuns)}`);

    if (uniqueDuns.length === 0) {
      logger.warn({ area: 'plans' }, 'Electricity Plans - No DUNS found in 2TurnItOn plans');
      return { success: false, error: 'No DUNS data available' };
    }

    // Step 3: Fetch ComparePower for each DUNS
    const cpResults = await Promise.allSettled(
      uniqueDuns.map((duns) => fetchComparePowerForDuns(duns, estimatedUsage))
    );

    const allCpPlans: ComparePowerPlan[] = [];
    cpResults.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        allCpPlans.push(...result.value);
      } else {
        logger.warn({ area: 'plans' }, `Electricity Plans - Failed for DUNS ${uniqueDuns[index]}`);
      }
    });

    if (allCpPlans.length === 0) {
      logger.warn({ area: 'plans' }, 'Electricity Plans - No plans from ComparePower');
      return { success: false, error: 'No pricing data available' };
    }

    logger.info({ area: 'plans' }, `Electricity Plans - ComparePower plans: ${allCpPlans.length}`);

    // Step 4: Match and merge by offerCode === _id
    const mergedPlans: MergedElectricityPlan[] = [];

    turnItOnPlans.forEach((tioPlan) => {
      if (!tioPlan.offerCode) return;

      const cpPlan = allCpPlans.find((cp) => cp._id === tioPlan.offerCode);
      if (!cpPlan) return;

      mergedPlans.push({
        id: tioPlan.ID || tioPlan.id || tioPlan.offerCode,
        vendorId: tioPlan.vendorId,
        vendorName: tioPlan.vendorName,
        name: cpPlan.product?.name || tioPlan.name,
        logo: tioPlan.logo,
        pricePerKwh: cpPlan.average_cents_per_kWh / 100,
        pricePerKwhCents: cpPlan.average_cents_per_kWh,
        totalCost: cpPlan.total_cost,
        estimatedMonthlyCost: cpPlan.total_cost,
        termLength: cpPlan.product?.term,
        term: cpPlan.product?.term,
        percentGreen: cpPlan.product?.percent_green || 0,
        earlyTerminationFee: cpPlan.product?.early_termination_fee,
        shortDescription: cpPlan.product?.headline,
        documentLinks: cpPlan.document_links?.map((d) => ({ name: d.name, url: d.url })),
        ...normalizeSchedule(tioPlan),
      });
    });

    logger.info({ area: 'plans' }, `Electricity Plans - Matched plans: ${mergedPlans.length}`);

    if (mergedPlans.length === 0) {
      return { success: false, error: 'No matching plans found' };
    }

    // Step 5: Select top 3 plans
    const selectedPlans = selectTopPlans(mergedPlans);
    logPlanScheduleDiagnostics({
      serviceName: 'Electricity',
      stage: 'selected-merged-plans',
      zipCode: addressData.zip,
      userId,
      plans: selectedPlans,
    });

    // Normalize all plan IDs before returning
    return {
      success: true,
      plans: selectedPlans.map(normalizePlanId),
      estimatedUsage,
      usageSource,
    };
  } catch (error) {
    logger.error({ area: 'plans' }, 'Electricity Plans Error', error instanceof Error ? error : undefined);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
}

// ============================================
// Usage Profile from ERCOT (unchanged)
// ============================================

export async function fetchUsageProfile(esiid: string): Promise<UsageProfile | null> {
  try {
    const response = await api.get<{
      success: boolean;
      usage?: number[];
      averageUsage?: number;
      annualUsage?: number;
    }>(
      `/api/ercot/usage-profile/${encodeURIComponent(esiid)}`
    );

    if (response.success && response.data) {
      const data = response.data;
      if (data.success && data.usage) {
        return {
          usage: data.usage,
          averageUsage: data.averageUsage,
          annualUsage: data.annualUsage,
        };
      }
    }

    return null;
  } catch (error) {
    logger.error({ area: 'plans' }, 'Usage Profile Error', error instanceof Error ? error : undefined);
    return null;
  }
}
